import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { fetchCategoryData } from "app/pages/industry-analysis/common/queries/shared/industryAnalysisQueryUtils";
import type { ICategory } from "app/common/services/categoryService.types";
import { timeGranularity } from "app/components/widget/widget-utilities/time-granularity";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import { default as DurationService, BasicDurations } from "app/services/DurationService";
import { calcChange } from "app/UtilitiesAndConstants/UtilityFunctions/change";
import { EPeriodSupport } from "app/UtilitiesAndConstants/UtilityFunctions/periodSupport";

import { MARKET_SIZE_ENDPOINTS } from "../consts";
import type { MarketSizeDataPoint, MarketSizeDataResponse, MarketSizeScoreResponse } from "../types";
import { getFromTo, getUserSupportedPeriod } from "../utils";

export const processUniqueData = (
    uniqueResponse: MarketSizeDataResponse | undefined,
    category: ICategory,
    webSource: string,
    periodSupport: EPeriodSupport,
): {
    uniqueData: MarketSizeDataPoint[] | undefined;
    absoluteUniqueValue: string | undefined;
    changeData: number | undefined;
} => {
    if (!uniqueResponse || !category) {
        return {
            uniqueData: undefined,
            absoluteUniqueValue: undefined,
            changeData: undefined,
        };
    }

    const visitsFilter = minVisitsAbbrFilter();
    const categoryData = uniqueResponse.Data?.[category.forApi];

    if (!categoryData) {
        return {
            uniqueData: undefined,
            absoluteUniqueValue: undefined,
            changeData: undefined,
        };
    }

    const platformData = categoryData[webSource as keyof typeof categoryData];

    if (!platformData || !Array.isArray(platformData) || !platformData[0]) {
        return {
            uniqueData: undefined,
            absoluteUniqueValue: undefined,
            changeData: undefined,
        };
    }

    const dataArr = platformData[0] as MarketSizeDataPoint[];
    const dataArrLength = dataArr.length;
    const absoluteValue = dataArr[dataArrLength - 1]?.Value;
    const isMonthly = periodSupport === EPeriodSupport.Monthly;
    let change: number | undefined;

    if (dataArrLength > 1) {
        // For monthly period, compare to previous month; otherwise compare to first data point
        if (isMonthly) {
            change = calcChange(dataArr[dataArrLength - 1].Value, dataArr[dataArrLength - 2].Value);
        } else {
            change = calcChange(dataArr[dataArrLength - 1].Value, dataArr[0].Value);
        }
    }

    return {
        uniqueData: dataArr,
        absoluteUniqueValue: visitsFilter(absoluteValue) as string,
        changeData: change,
    };
};

export const processScoreData = (scoreResponse: MarketSizeScoreResponse | undefined): string | undefined => {
    if (!scoreResponse) {
        return undefined;
    }

    const percentageFilter = percentageSignFilter();
    return percentageFilter(scoreResponse.Score, 2) as string;
};

export interface IMarketSizeResult {
    uniqueData: MarketSizeDataPoint[] | undefined;
    absoluteUniqueValue: string | undefined;
    scoreData: string | undefined;
    changeData: number | undefined;
    periodSupport: EPeriodSupport;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
}

export const useMarketSizeData = (category: ICategory | null): IMarketSizeResult => {
    const { params } = useCurrentStateAndParams();
    const { country, comparedDuration, webSource, includeSubdomains = true } = params;
    const periodSupport = useMemo(() => getUserSupportedPeriod(), []);

    const durations = DurationService.getDurationData(BasicDurations.LAST_MONTH, comparedDuration as string);
    const { isWindow, compareFrom, compareTo } = durations.forAPI;
    const { from, to } = getFromTo();

    const baseQueryParams = {
        category: category?.forDisplayApi,
        categoryHash: category?.categoryHash,
        country: country as string,
        isWindow,
        keys: category?.forApi,
        from,
        to,
        webSource: webSource as string,
        timeGranularity: timeGranularity.monthly,
        includeSubdomains,
    };
    const scoreQueryParams = {
        ...baseQueryParams,
        compareFrom,
        compareTo,
    };

    const uniqueQueryKey = ["marketSize", "unique", baseQueryParams];
    const scoreQueryKey = ["marketSize", "score", scoreQueryParams];

    const uniqueQuery = useQuery({
        queryKey: uniqueQueryKey,
        queryFn: () =>
            fetchCategoryData<MarketSizeDataResponse>(MARKET_SIZE_ENDPOINTS.unique, category, baseQueryParams),
        select: (data: MarketSizeDataResponse) => {
            if (!category) {
                return { uniqueData: undefined, absoluteUniqueValue: undefined, changeData: undefined };
            }
            return processUniqueData(data, category, webSource as string, periodSupport);
        },
        enabled: !!category,
    });

    const scoreQuery = useQuery({
        queryKey: scoreQueryKey,
        queryFn: () =>
            fetchCategoryData<MarketSizeScoreResponse>(MARKET_SIZE_ENDPOINTS.score, category, scoreQueryParams),
        select: (data: MarketSizeScoreResponse) => processScoreData(data),
        enabled: !!category,
    });

    return {
        uniqueData: uniqueQuery.data?.uniqueData,
        absoluteUniqueValue: uniqueQuery.data?.absoluteUniqueValue,
        changeData: uniqueQuery.data?.changeData,
        scoreData: scoreQuery.data,
        periodSupport,
        isLoading: uniqueQuery.isLoading || scoreQuery.isLoading,
        isError: uniqueQuery.isError || scoreQuery.isError,
        error: uniqueQuery.error || scoreQuery.error,
    };
};
